import { useMemo } from 'react';

import { cn, Skeleton, TableCell, TableRow } from '@crossnokaye/ux-common';

import { CompressorEnergyAIStageRow } from './components/compressor-energy-ai-stage-row';
import { CompressorEnergyAIStageTable } from './components/compressor-energy-ai-stage-table';
import { ENERGY_AI_COLUMN_CLASS } from './compressor-energy-ai.constants';
import { useCompressorEnergyAIStageRows } from './hooks/use-compressor-energy-ai-stage-rows';

interface CompressorEnergyAIProps {
  compressorOrder: string[];
  cosmicActive: boolean | undefined;
  lockedCompressorNames: string[];
  unavailableCompressorNames: string[];
}

/**
 * The ENERGY AI stage column rendered beside the compressor sequencer table,
 * showing what Atlas control has each compressor doing right now.
 */
export const CompressorEnergyAI = ({
  compressorOrder,
  cosmicActive,
  lockedCompressorNames,
  unavailableCompressorNames,
}: CompressorEnergyAIProps) => {
  const { hasEnergyAiSequence, hasForecastData, isForecastError, isLoading, rows } =
    useCompressorEnergyAIStageRows({ compressorOrder });

  const lockedCompressorNameSet = useMemo(
    () => new Set(lockedCompressorNames),
    [lockedCompressorNames]
  );
  const unavailableCompressorNameSet = useMemo(
    () => new Set(unavailableCompressorNames),
    [unavailableCompressorNames]
  );

  const isEnergyAIActive = cosmicActive === true;
  const showForecastUnavailable = isForecastError || (!isLoading && !hasForecastData);

  if (!hasEnergyAiSequence || compressorOrder.length === 0) {
    return null;
  }

  const isAwaitingFirstForecast = isLoading && !hasForecastData;

  return (
    // This wrapper is the panel boundary: it gives the orchestrator a root of its
    // own to annotate, which it otherwise lacks by delegating straight to the
    // table. It carries the flex sizing so the inner table stays a plain block.
    <div
      className="shrink-0"
      data-component="CompressorEnergyAI"
      data-testid="compressor-energy-ai"
    >
      <CompressorEnergyAIStageTable
        isEnergyAIActive={isEnergyAIActive}
        showForecastUnavailable={showForecastUnavailable}
      >
        {isAwaitingFirstForecast
          ? compressorOrder.map((compressorName) => (
              <TableRow key={compressorName}>
                <TableCell className="h-[inherit] border-b-1 border-l-1 border-black p-0">
                  <Skeleton className={cn('h-full bg-ckds-neutral-700', ENERGY_AI_COLUMN_CLASS)} />
                </TableCell>
              </TableRow>
            ))
          : rows.map((row) => (
              <CompressorEnergyAIStageRow
                isEnergyAIActive={isEnergyAIActive}
                isLockedByOther={lockedCompressorNameSet.has(row.compressorName)}
                isUnavailableInSequence={unavailableCompressorNameSet.has(row.compressorName)}
                key={row.compressorName}
                row={row}
              />
            ))}
      </CompressorEnergyAIStageTable>
    </div>
  );
};
